import {
  Avatar,
  ButtonDS,
  ErrorOutlineOutlined,
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  MessageTooltip,
  Spacer,
  TableTagLinkyConsentStatus,
  TableTagVehicleEnergyType,
  usePersistMuiTableState,
  useUrlTableFilter,
} from "@qivia/ui";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { formatDate } from "../../../libSlice/materialUiFormats";
import {
  getLinkyConsentsAsync,
  selectLinkyConsents,
  selectLinkyConsentsStatus,
} from "./linkyConsentsSlice";
import { LinkyConsentDisplayed, LinkyConsentStatus } from "./linkyConsentsAPI";
import "../../../i18n/i18n";

export const LinkyConnectionsTab = () => {
  const { t } = useTranslation();
  const params = useParams();
  const dispatch = useAppDispatch();
  const linkyConsents = useAppSelector(selectLinkyConsents);
  const linkyConsentsStatus = useAppSelector(selectLinkyConsentsStatus);
  const [listDisplayed, setListDisplayed] = useState<
    LinkyConsentDisplayed[] | null
  >(null);

  const pageName = "linky_consents";
  const apiRef = useGridApiRef();
  const dataGridState = usePersistMuiTableState(
    apiRef,
    "customers-grid",
    pageName,
  );

  useEffect(() => {
    if (linkyConsentsStatus === "idle") {
      void dispatch(getLinkyConsentsAsync());
    }
  }, [dispatch, linkyConsentsStatus]);

  const allConsentsDisplayed: LinkyConsentDisplayed[] = useMemo(() => {
    return linkyConsents.map((consent) => {
      const row: LinkyConsentDisplayed & { id: string } = {
        ...consent,
        id: consent.uuid,
      };
      return row;
    });
  }, [linkyConsents]);

  useEffect(() => {
    if (linkyConsentsStatus === "success") {
      setListDisplayed(allConsentsDisplayed);
    }
  }, [allConsentsDisplayed, linkyConsentsStatus]);

  const [searchParams] = useSearchParams();

  useUrlTableFilter(apiRef, !!listDisplayed, {
    value: searchParams.get("filterStatus"),
    field: "consentStatus",
    filterId: "linkyStatusUrlFilter",
  });

  const searchBarProps = {
    values: allConsentsDisplayed,
    setFilterValues: setListDisplayed,
    keysToIgnore: ["id" as const, "consentLinkUrl" as const],
  };

  const columnsDisplayed: {
    [index in keyof LinkyConsentDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      driverName: {
        type: "string",
        width: 250,
        valueFormatter: (value: string) => {
          if (!value) return <>-</>;
          const initials = value.split(" ").map((word) => word.charAt(0));
          return (
            <StyledRow>
              <Avatar
                color={"mint"}
                size={"S"}
                firstName={initials[0]}
                lastName={initials[initials.length - 1]}
              />
              <Spacer x={0.75} />
              {value}
            </StyledRow>
          );
        },
      },
      vehicleEnergyType: {
        type: "string",
        width: 200,
        renderCell: (p) => (
          <StyledCell>
            <TableTagVehicleEnergyType type={p.value as string} />
          </StyledCell>
        ),
      },
      consentStatus: {
        type: "string",
        width: 200,
        renderCell: (p) => (
          <StyledCell>
            <TableTagLinkyConsentStatus status={p.value as string} />
          </StyledCell>
        ),
      },
      connectionDate: {
        type: "string",
        width: 200,
        valueFormatter: (value: string) => (value ? formatDate(value) : "-"),
      },
      consentLinkUrl: {
        type: "string",
        width: 200,
        renderCell: (p) => {
          const status = p.row.consentStatus as LinkyConsentStatus;
          const url = p.row.consentLinkUrl as string | null;
          if (!url) {
            return <>-</>;
          }
          return (
            <StyledCell>
              <ButtonDS
                text={
                  status === "PENDING"
                    ? t("linky_consents.action.resend")
                    : t("linky_consents.action.send")
                }
                buttonType={status === "PENDING" ? "secondary" : "primary"}
                format={"hug"}
                onClick={() => console.log(url)}
              />
            </StyledCell>
          );
        },
      },
    };
  }, [t]);

  if (params.tab !== "linky_connections") {
    return null;
  }

  return (
    <StyledContainer>
      <MessageTooltip
        type={"NEUTRAL"}
        iconLeft={<ErrorOutlineOutlined />}
        title={t("linky_consents.banner")}
      />
      <Spacer y={2} />
      {!listDisplayed ? (
        <LaunchPage hasBorderRadius={true} />
      ) : (
        <MaterialUiTable<keyof LinkyConsentDisplayed, LinkyConsentDisplayed>
          rows={listDisplayed}
          columns={columnsDisplayed}
          pageName={pageName}
          dataGridState={dataGridState}
          searchBar={searchBarProps}
          apiRef={apiRef}
        />
      )}
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const StyledCell = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
`;

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;
